import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Search,
  Edit,
  Mail,
  Phone,
  ArrowUp,
  ArrowDown,
  Loader2,
  CheckCircle,
  XCircle,
  ArrowRight,
  Users as UsersIcon,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/contexts/AuthContext";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { apiCall } from "@/utils/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface User {
  id: number;
  kommo_user_id: number;
  name: string;
  email: string;
  phone_number?: string;
  role?: string;
  is_active: boolean;
  active_for_dispatch?: boolean;
  is_online?: boolean;
  assignment_order?: number;
  dispatch_order?: number;
  notifications?: {
    email: boolean;
    whatsapp: boolean;
  };
  kommo_rights?: {
    leads?: {
      view?: string;
      edit?: string;
      add?: string;
      delete?: string;
      export?: string;
    };
    contacts?: {
      view?: string;
      edit?: string;
      add?: string;
      delete?: string;
      export?: string;
    };
    companies?: {
      view?: string;
      edit?: string;
      add?: string;
      delete?: string;
      export?: string;
    };
    tasks?: {
      edit?: string;
      delete?: string;
    };
    mail_access?: boolean;
    catalog_access?: boolean;
    files_access?: boolean;
    is_admin?: boolean;
    status_rights?: any[];
    catalog_rights?: any;
    custom_fields_rights?: any;
    is_free?: boolean;
    group_id?: any;
    role_id?: number;
  };
}

interface AddUserForm {
  name: string;
  email: string;
  phone_number: string;
  email_notifications: boolean;
  whatsapp_notifications: boolean;
}

interface EditUserForm {
  user_id: number;
  phone_number: string;
  email_notifications: boolean;
  whatsapp_notifications: boolean;
}

const Users: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [updatingPhone, setUpdatingPhone] = useState<boolean>(false);

  const { token } = useAuth();

  const [addForm, setAddForm] = useState<AddUserForm>({
    name: "",
    email: "",
    phone_number: "",
    email_notifications: true,
    whatsapp_notifications: true,
  });

  const [editForm, setEditForm] = useState<EditUserForm>({
    user_id: 0,
    phone_number: "",
    email_notifications: false,
    whatsapp_notifications: false,
  });

  const [showPhoneModal, setShowPhoneModal] = useState<boolean>(false);
  const [editingPhoneUser, setEditingPhoneUser] = useState<User | null>(null);
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [showProcessingModal, setShowProcessingModal] = useState<boolean>(false);
  const [syncing, setSyncing] = useState<boolean>(false);

  // Memoize filtered users to prevent unnecessary recalculations
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.role &&
            user.role.toLowerCase().includes(searchTerm.toLowerCase()))
      ),
    [users, searchTerm]
  );

  const fetchUsers = async (syncFirst: boolean = false) => {
    try {
      setLoading(true);

      // If syncFirst is true, first sync from Kommo
      if (syncFirst) {
        setSyncing(true);
        try {
          const syncResult = await apiCall<{
            success: boolean;
            message?: string;
            stats?: { total: number; created: number; updated: number };
          }>(token, "/sellers/sync", "POST");

          if (syncResult.success) {
            toast.success("Synchronisation réussie", {
              description: syncResult.message || "Les commerciaux ont été synchronisés depuis Kommo"
            });
          } else {
            toast.error("Erreur de synchronisation", {
              description: syncResult.message || "Impossible de synchroniser depuis Kommo"
            });
          }
        } catch (syncErr) {
          console.error("Error syncing from Kommo:", syncErr);
          toast.error("Erreur de synchronisation", {
            description: "Une erreur est survenue lors de la synchronisation depuis Kommo"
          });
        } finally {
          setSyncing(false);
        }
      }

      // Then fetch the list
      const result = await apiCall<{
        success: boolean;
        sellers: User[];
        message?: string;
      }>(token, "/sellers");

      if (result.success) {
        setUsers(result.sellers || []);

      } else {
        setError("Failed to load users");
        setUsers([]);
        toast.error("Erreur de chargement", {
          description: "Impossible de charger la liste des commerciaux"
        });
      }
    } catch (err) {
      setError("Failed to load users");
      console.error("Error fetching users:", err);
      setUsers([]);
      toast.error("Erreur de chargement", {
        description: "Une erreur est survenue lors du chargement des commerciaux"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = useCallback(
    async (userId: number) => {
      try {
        setShowProcessingModal(true);
        const result = await apiCall<{ success: boolean; message?: string }>(
          token,
          "/sellers",
          "POST",
          {
            action: "toggle_user_status",
            user_id: userId,
          }
        );

        if (result.success) {
          fetchUsers();
        } else {
          setError(result.message || "Failed to toggle user status");
          toast.error("Erreur de mise à jour", {
            description: result.message || "Impossible de changer le statut du commercial"
          });
        }
      } catch (err) {
        setError("Failed to toggle user status");
        console.error("Error toggling user status:", err);
        toast.error("Erreur de mise à jour", {
          description: "Une erreur est survenue lors du changement de statut"
        });
      } finally {
        setShowProcessingModal(false);
      }
    },
    [token]
  );

  const handleMoveUser = useCallback(
    async (userId: number, direction: "up" | "down") => {
      try {
        const result = await apiCall<{ success: boolean; message?: string }>(
          token,
          "/sellers",
          "POST",
          {
            action: "move_user_order",
            user_id: userId,
            direction: direction,
          }
        );

        if (result.success) {
          toast.success("Ordre mis à jour", {
            description: "Le commercial a été déplacé avec succès"
          });
          fetchUsers();
        } else {
          setError(result.message || "Failed to move user");
          toast.error("Erreur de réorganisation", {
            description: result.message || "Impossible de déplacer le commercial"
          });
        }
      } catch (err) {
        setError("Failed to move user");
        console.error("Error moving user:", err);
        toast.error("Erreur de réorganisation", {
          description: "Une erreur est survenue lors du déplacement"
        });
      }
    },
    [token]
  );

  const openEditModal = useCallback((user: User) => {
    setEditingUser(user);
    setEditForm({
      user_id: user.id,
      phone_number: user.phone_number || "",
      email_notifications: user.notifications?.email || false,
      whatsapp_notifications: user.notifications?.whatsapp || false,
    });
    setShowEditModal(true);
  }, []);

  const handleEditPhone = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPhoneUser) return;

    try {
      setUpdatingPhone(true);
      const result = await apiCall<{ success: boolean; message?: string }>(
        token,
        `/sellers/${editingPhoneUser.kommo_user_id}/phone`,
        "PUT",
        {
          phone_number: phoneNumber,
        }
      );

      if (result.success) {
        setShowPhoneModal(false);
        setEditingPhoneUser(null);
        setPhoneNumber("");
        setUpdatingPhone(false);
        setUsers((prevUsers) => {
          const index = prevUsers.findIndex(
            (u) => u.id === editingPhoneUser.id
          );
          if (index === -1) return prevUsers;

          const newUsers = [...prevUsers];
          newUsers[index].phone_number = phoneNumber;
          return newUsers;
        });

        toast.success("Numéro de téléphone mis à jour", {
          description: `Le numéro de ${editingPhoneUser.name} a été modifié avec succès`
        });
      } else {
        setError(result.message || "Failed to update phone number");
        toast.error("Erreur de mise à jour", {
          description: result.message || "Impossible de mettre à jour le numéro de téléphone"
        });
      }
    } catch (err) {
      setError("Failed to update phone number");
      console.error("Error updating phone number:", err);
      toast.error("Erreur de mise à jour", {
        description: "Une erreur est survenue lors de la mise à jour du numéro"
      });
    }
  };

  const openPhoneModal = (user: User) => {
    setEditingPhoneUser(user);
    setPhoneNumber(user.phone_number || "");
    setShowPhoneModal(true);
  };

  // Initial data fetch - only run once on mount
  useEffect(() => {
    if (!token) return;

    const loadUsers = async () => {
      try {
        setLoading(true);
        const result = await apiCall<{
          success: boolean;
          sellers: User[];
          message?: string;
        }>(token, "/sellers");

        if (result.success) {
          setUsers(result.sellers || []);
        } else {
          setError("Failed to load users");
          setUsers([]);
          toast.error("Erreur de chargement initial", {
            description: "Impossible de charger la liste des commerciaux"
          });
        }
      } catch (err) {
        setError("Failed to load users");
        console.error("Error fetching users:", err);
        setUsers([]);
        toast.error("Erreur de chargement initial", {
          description: "Une erreur est survenue lors du chargement"
        });
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, [token]);

  // Simple components - no memoization needed
  const PermissionBadge = ({
    level,
    label,
  }: {
    level: string;
    label: string;
  }) => {
    const variants = {
      A: { className: "bg-green-100 text-green-800 border-green-200" },
      M: { className: "bg-blue-100 text-blue-800 border-blue-200" },
      D: { className: "bg-red-100 text-red-800 border-red-200" },
      "-": { className: "bg-gray-100 text-gray-600 border-gray-200" },
    };
    const variant = variants[level as keyof typeof variants] || variants["-"];
    return (
      <span
        className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded border ${variant.className}`}
      >
        {label}
      </span>
    );
  };

  const PermissionRow = ({ label, value }: { label: string; value: string }) =>
    value === "D" ? null : (
      <div className="flex items-center gap-2 text-xs">
        <PermissionBadge
          level={value}
          label={
            label +
            " " +
            (value === "A"
              ? "All"
              : value === "M"
                ? "Mine"
                : value === "D"
                  ? "Denied"
                  : "None")
          }
        />
      </div>
    );

  const EntityPermissions = ({
    title,
    entity,
  }: {
    title: string;
    entity: any;
  }) => {
    if (!entity) return null;
    return (
      <div className="mb-2 flex gap-4 items-center">
        <div className="font-medium text-xs text-gray-700 mb-1">{title}</div>
        <div className="flex gap-2">
          <PermissionRow label="View" value={entity.view || "-"} />
          <PermissionRow label="Edit" value={entity.edit || "-"} />
          <PermissionRow label="Add" value={entity.add || "-"} />
          <PermissionRow label="Delete" value={entity.delete || "-"} />
          <PermissionRow label="Export" value={entity.export || "-"} />
        </div>
      </div>
    );
  };

  const getStatusBadge = (
    isActive: boolean
  ): "default" | "destructive" | "outline" | "secondary" | null | undefined => {
    return isActive ? "default" : "destructive";
  };

  const getRoleBadge = (role?: string) => {
    const colors: Record<string, string> = {
      Admin: "text-red-700 bg-red-50 border-red-100",
      "Sales Manager": "text-blue-700 bg-blue-50 border-blue-100",
      "Sales Rep": "text-indigo-700 bg-indigo-50 border-indigo-100",
      Seller: "text-slate-700 bg-slate-50 border-slate-100",
    };

    return colors[role || "Sales Rep"] || "text-slate-600 bg-slate-100 border-slate-200";
  };

  return (
    <div className="container mx-auto py-8 px-4 space-y-8 animate-in fade-in duration-700">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-2xl">
              <UsersIcon className="h-6 w-6 text-primary" />
            </div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 uppercase">Équipe Commerciale</h1>
          </div>
          <p className="text-slate-500 font-medium ml-14">Gérez vos commerciaux, synchronisez les données Kommo et optimisez le dispatch.</p>
        </div>
        <div className="flex items-center gap-3 ml-14 md:ml-0">
          <Button
            variant="outline"
            onClick={() => fetchUsers(true)}
            disabled={loading || syncing}
            className="rounded-2xl h-12 px-6 font-bold bg-white shadow-xl shadow-slate-200/50 border-slate-100 hover:bg-slate-50 transition-all hover:scale-105 active:scale-95"
          >
            <Loader2 className={cn("h-4 w-4 mr-2", (loading || syncing) ? "animate-spin" : "hidden")} />
            <RefreshCw className={cn("h-4 w-4 mr-2", (loading || syncing) ? "hidden" : "")} />
            {syncing ? "Synchronisation..." : "Actualiser Kommo"}
          </Button>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {[
          { label: "Total Équipiers", value: users.length, icon: UsersIcon, color: "text-slate-900", bg: "bg-slate-50" },
          { label: "Actifs Dispatch", value: users.filter((u) => u.active_for_dispatch).length, icon: CheckCircle, color: "text-emerald-600", bg: "bg-emerald-50" },
          { label: "Mode Dispatch", value: "Séquentiel", icon: ArrowDown, color: "text-blue-600", bg: "bg-blue-50" },
        ].map((stat, i) => (
          <Card key={i} className="border-none shadow-2xl shadow-slate-200/40 rounded-[2rem] overflow-hidden bg-white/80 backdrop-blur-xl group transition-all hover:translate-y-[-4px]">
            <CardContent className="p-8 flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">{stat.label}</p>
                <div className={cn("text-4xl font-black", stat.color)}>{loading ? <Loader2 className="h-8 w-8 animate-spin" /> : stat.value}</div>
              </div>
              <div className={cn("p-4 rounded-2xl transition-transform group-hover:scale-110", stat.bg)}>
                <stat.icon className={cn("h-6 w-6", stat.color)} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="border-none shadow-2xl shadow-slate-200/40 rounded-[2.5rem] overflow-hidden bg-white/80 backdrop-blur-xl">
        <CardHeader className="p-8 pb-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="relative flex-1 max-w-md group">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-primary transition-colors" />
              <Input
                placeholder="Rechercher par nom, email, rôle..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 h-12 bg-slate-50 border-slate-100 rounded-2xl focus:ring-primary/20 focus:border-primary/30 transition-all text-sm font-medium"
              />
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="h-10 px-4 rounded-xl border-slate-100 bg-white font-bold text-slate-400 uppercase tracking-widest text-[9px]">
                {filteredUsers.length} COLLABORATEURS TROUVÉS
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
                <p className="text-slate-500 font-bold">Chargement des données...</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-50 hover:bg-transparent">
                    <TableHead className="w-[120px] pl-8 text-[11px] font-black uppercase tracking-widest text-slate-400 text-center">Ordre de Dispatch</TableHead>
                    <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Collaborateur / Nom Complet</TableHead>
                    <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Contact</TableHead>
                    <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Rôle Kommo</TableHead>
                    <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400">Droits d'accès</TableHead>
                    <TableHead className="text-[11px] font-black uppercase tracking-widest text-slate-400 text-center">Status</TableHead>
                    <TableHead className="w-[180px] pr-8 text-right text-[11px] font-black uppercase tracking-widest text-slate-400">Management</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="h-32 text-center text-slate-400 font-medium">
                        Aucun commercial trouvé pour cette recherche.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredUsers.map((user, index) => (
                      <TableRow
                        key={user.id}
                        className={cn(
                          "border-slate-50 hover:bg-slate-50/50 transition-colors group",
                          user.active_for_dispatch ? "" : "opacity-60 grayscale-[0.5]"
                        )}
                      >
                        <TableCell className="pl-8 py-6">
                          <div className="flex flex-col items-center">
                            {(user.dispatch_order ?? -1) > 0 ? (
                              <div className="flex flex-col items-center gap-0.5">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 text-slate-300 hover:text-primary hover:bg-primary/5 transition-all"
                                  onClick={() => handleMoveUser(user.kommo_user_id, "up")}
                                  disabled={index === 0}
                                >
                                  <ArrowUp className="h-3 w-3" />
                                </Button>
                                <span className="text-xs font-black text-primary bg-primary/10 w-8 h-8 rounded-xl flex items-center justify-center border border-primary/20 shadow-sm group-hover:scale-110 transition-transform">
                                  {user.dispatch_order}
                                </span>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-slate-400 hover:text-primary hover:bg-primary/5 rounded-full transition-all"
                                  onClick={() => handleMoveUser(user.kommo_user_id, "down")}
                                  disabled={index === filteredUsers.filter(u => u.active_for_dispatch).length - 1}
                                >
                                  <ArrowDown className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-400 hover:bg-slate-100 border-none font-black text-[9px] px-2 py-1 rounded-lg">OFFLINE</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-4">
                            <div className="relative">
                              <Avatar className="h-12 w-12 rounded-[1rem] border-2 border-white shadow-xl ring-1 ring-slate-100">
                                <AvatarFallback className="bg-primary/5 text-primary font-black text-xs uppercase">
                                  {user.name?.split(" ").map((n) => n[0]).join("") || "U"}
                                </AvatarFallback>
                              </Avatar>
                              <div className={cn(
                                "absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 border-white shadow-sm",
                                user.is_online ? "bg-emerald-500" : "bg-slate-300"
                              )} />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-slate-900 text-sm tracking-tight group-hover:text-primary transition-colors">
                                  {user.name || "N/A"}
                                </span>
                              </div>
                              <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest flex items-center gap-1.5 mt-0.5">
                                <Mail className="h-3 w-3" />
                                {user.email}
                              </div>
                            </div>
                          </div>
                        </TableCell>

                        <TableCell>
                          <div className="group/phone flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-600">
                              {user.phone_number || "N/A"}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 opacity-0 group-hover/phone:opacity-100 hover:bg-primary/5 hover:text-primary rounded-lg transition-all"
                              onClick={() => openPhoneModal(user)}
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </TableCell>

                        <TableCell>
                          <Badge className={cn("text-[9px] font-black uppercase tracking-widest rounded-xl border-none shadow-sm px-3 py-1", getRoleBadge(user.role))}>
                            {user.kommo_rights?.is_admin ? "🔥 Administrateur" : "👤 Vendeur"}
                          </Badge>
                        </TableCell>

                        <TableCell>
                          <div className="flex flex-col gap-1.5 max-w-[200px]">
                            {user.kommo_rights ? (
                              <>
                                <div className="flex gap-2">
                                  <span className="text-[10px] font-bold text-slate-400 w-10 uppercase">Leads</span>
                                  <div className="flex gap-1">
                                    <PermissionBadge level={user.kommo_rights.leads?.view || "-"} label="V" />
                                    <PermissionBadge level={user.kommo_rights.leads?.edit || "-"} label="E" />
                                    <PermissionBadge level={user.kommo_rights.leads?.add || "-"} label="A" />
                                  </div>
                                </div>
                                <div className="flex gap-2">
                                  <span className="text-[10px] font-bold text-slate-400 w-10 uppercase">Contacts</span>
                                  <div className="flex gap-1">
                                    <PermissionBadge level={user.kommo_rights.contacts?.view || "-"} label="V" />
                                    <PermissionBadge level={user.kommo_rights.contacts?.edit || "-"} label="E" />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <span className="text-[10px] font-bold text-slate-300 italic">Données Kommo indisponibles</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className={cn(
                            "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase ring-1 ring-inset",
                            user.active_for_dispatch
                              ? "bg-emerald-50 text-emerald-700 ring-emerald-600/10"
                              : "bg-slate-50 text-slate-500 ring-slate-400/10"
                          )}>
                            <div className={cn("w-1.5 h-1.5 rounded-full animate-pulse", user.active_for_dispatch ? "bg-emerald-500" : "bg-slate-400")} />
                            {user.active_for_dispatch ? "Actif" : "À l'arrêt"}
                          </div>
                        </TableCell>
                        <TableCell className="text-right pr-8">
                          <Button
                            variant={user.active_for_dispatch ? "outline" : "default"}
                            size="sm"
                            className={cn(
                              "h-10 px-5 font-black uppercase tracking-tighter rounded-2xl transition-all shadow-none text-[10px]",
                              user.active_for_dispatch
                                ? "text-slate-400 hover:text-red-500 hover:bg-red-50 border-slate-100"
                                : "bg-primary shadow-lg shadow-primary/20 text-white hover:scale-105 active:scale-95"
                            )}
                            onClick={() => handleToggleStatus(user.kommo_user_id)}
                          >
                            {user.active_for_dispatch ? "Désactiver" : "Activer Dispatch"}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Phone Edit Modal */}
      <Dialog open={showPhoneModal} onOpenChange={setShowPhoneModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le numéro de téléphone</DialogTitle>
            <DialogDescription>
              Modifiez le numéro de téléphone pour {editingPhoneUser?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditPhone}>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="phone">Numéro de téléphone</Label>
                <Input
                  id="phone"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="Entrez le numéro de téléphone"
                  disabled={updatingPhone}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPhoneModal(false)}
                disabled={updatingPhone}
              >
                Annuler
              </Button>
              <Button type="submit" disabled={updatingPhone}>
                {updatingPhone ? (
                  <div className="flex items-center gap-2">
                    <span>Enregistrement...</span>{" "}
                    <Loader2 className="h-4 w-4 animate-spin" />
                  </div>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showProcessingModal}>
        <DialogContent className="[&>button]:hidden">
          <DialogHeader>
            <DialogTitle>Traitement en cours</DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              Veuillez patienter pendant que nous traitons votre requête <Loader2 className="h-4 w-4 animate-spin" />
            </DialogDescription>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Users;
